import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';

import { CategoryRestrictionsProfileConfiguration } from '@era/settings-sdk';
import { Category } from '../router/category';
import * as fromRestrictionProfilePresets from './category-restriction-profile-preset-configuration.reducer';
import { CategoryRestrictionProfilePresetConfigurationState } from './category-restriction-profile-preset-configuration.state';

// ============================================================================
// STATE SELECTORS
// ============================================================================

export const selectRestrictionProfilePresetConfigurationState =
  createFeatureSelector<CategoryRestrictionProfilePresetConfigurationState>(
    'categoryRestrictionProfilePresetConfiguration'
  );

export const selectRestrictionProfilePresetConfigurationIds = createSelector(
  selectRestrictionProfilePresetConfigurationState,
  fromRestrictionProfilePresets.selectRestrictionProfilePresetConfigurationIds
);

export const selectRestrictionProfilePresetConfigurationEntities = createSelector(
  selectRestrictionProfilePresetConfigurationState,
  fromRestrictionProfilePresets.selectRestrictionProfilePresetConfigurationEntities
);

export const selectAllRestrictionProfilePresetConfigurations = createSelector(
  selectRestrictionProfilePresetConfigurationState,
  fromRestrictionProfilePresets.selectAllRestrictionProfilePresetConfigurations
);

// // ============================================================================
// // GENERIC SELECTORS
// // ============================================================================

// Generic selector factory functions that work with any CategoryType
// These eliminate the need to replicate selectors for every category type

export const selectRestrictionProfilePresetConfigurationsLinkedToCategory = (category: Category) => {
  return createSelector(
    selectAllRestrictionProfilePresetConfigurations,
    (configurations: CategoryRestrictionsProfileConfiguration[]) => {
      if (!category) {
        return [];
      }

      // Filter configurations that match both categoryId and categoryType
      // This selector only checks direct links, inheritance is handled in selectRestrictionProfilePresetConfigurationsAppliedToCategory
      return configurations.filter(
        (configuration) =>
          configuration.categoryId === category.categoryId && configuration.categoryType === category.categoryType
      );
    }
  );
};

export const selectRestrictionProfilePresetConfigurationsLoadedForCategory = (category: Category) => {
  return createSelector(
    selectAllRestrictionProfilePresetConfigurations,
    (configurations: CategoryRestrictionsProfileConfiguration[]) => {
      if (!category) {
        return false;
      }

      // Only check the specific category - don't check parents
      // Each category should load its own data, inheritance is handled by display selectors
      return configurations.some(
        (configuration) =>
          configuration.categoryId === category.categoryId && configuration.categoryType === category.categoryType
      );
    }
  );
};

// ============================================================================
// RESTRICTION PROFILE PRESET APPLIED TO CATEGORY SELECTORS
// ============================================================================

/**
 * Select restriction profile preset configurations applied to category (with inheritance).
 * Automatically handles inheritance logic for Brand -> Location -> Room/Kiosk.
 */
export const selectRestrictionProfilePresetConfigurationsAppliedToCategory = (
  category: Category
): MemoizedSelector<object, CategoryRestrictionsProfileConfiguration[]> => {
  if (category.parent) {
    return createSelector(
      selectRestrictionProfilePresetConfigurationsAppliedToCategory(category.parent),
      selectRestrictionProfilePresetConfigurationsLinkedToCategory(category),
      (parent, current) =>
        Array.from(new Map([...parent, ...current].map((i) => [i.restrictionsProfilePresetId, i])).values())
    );
  } else {
    return selectRestrictionProfilePresetConfigurationsLinkedToCategory(category);
  }
};
